import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import { INTEGRATION_AUTH } from '@app/constants';

import {
  IntegrationAuthEndpoints as Endpoints,
  type GetQrRequest,
  type GetQrResponse,
  type RequestQrRequest,
  type RequestQrResponse,
  type SendAuthValueRequest,
  type SendAuthValueResponse,
} from './types';

/**
 * Локальный идентификатор попытки нужен только RTK Query: каждый успешно
 * начатый вход получает отдельную cache key и не может увидеть QR прошлой
 * попытки. В HTTP-параметры идентификатор не уходит.
 */
type GetQrAttemptRequest = GetQrRequest & {
  authAttemptId: number;
};

// CRM сама ограничивает получение QR общим сроком 30 секунд. Клиентский
// deadline обязан быть чуть больше серверного: при равных 30 сек браузер
// успевал отменить запрос во время финальной проверки статуса/доставки ответа.
export const AUTH_GATEWAY_REQUEST_TIMEOUT_MS = 35_000;

/**
 * Шаги входа единого окна подключения интеграций (us/10721).
 * Окно ходит только в CRM: шлюзы проксирует бэкенд (IntegrationAuthController).
 */
export const integrationAuthApi = createApi({
  reducerPath: 'integrationAuthApi',
  baseQuery: createApiBaseQuery(INTEGRATION_AUTH),
  endpoints: ({ query, mutation }) => ({
    [Endpoints.GetQr]: query<GetQrResponse, GetQrAttemptRequest>({
      query: ({ authAttemptId: _authAttemptId, ...params }) => ({
        url: Endpoints.GetQr,
        method: Methods.GET,
        params,
        timeout: AUTH_GATEWAY_REQUEST_TIMEOUT_MS,
      }),
    }),
    [Endpoints.RequestQr]: mutation<RequestQrResponse, RequestQrRequest>({
      query: (params) => ({
        url: Endpoints.RequestQr,
        method: Methods.POST,
        params,
        timeout: AUTH_GATEWAY_REQUEST_TIMEOUT_MS,
      }),
    }),
    [Endpoints.SendPhone]: mutation<
      SendAuthValueResponse,
      SendAuthValueRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SendPhone,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SendCode]: mutation<SendAuthValueResponse, SendAuthValueRequest>(
      {
        query: ({ data, ...params }) => ({
          url: Endpoints.SendCode,
          method: Methods.POST,
          params,
          data,
        }),
      }
    ),
    [Endpoints.SendPassword]: mutation<
      SendAuthValueResponse,
      SendAuthValueRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SendPassword,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetBotToken]: mutation<
      SendAuthValueResponse,
      SendAuthValueRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetBotToken,
        method: Methods.POST,
        params,
        data,
      }),
    }),
  }),
});

export const {
  useGetQrQuery,
  useLazyGetQrQuery,
  useRequestQrMutation,
  useSendPhoneMutation,
  useSendCodeMutation,
  useSendPasswordMutation,
  useSetBotTokenMutation,
} = integrationAuthApi;
